import { useApolloClient, useMutation } from '@apollo/client';
import { datadogLogs } from '@datadog/browser-logs';
import {
  BoundObjectType,
  type UseArchiveHouseholdCopyArchiveEntityBoundModelMutationVariables,
} from '@generated/graphql';
import { loadHouseholdOrganizationId } from '@ovComponents/4-module/workflowCompletion/subSteps/householdModelPortfolioDraft/householdDraftStaging';
import { isModelChangeProposalInFlight } from '@ovComponents/4-module/workflowCompletion/subSteps/modelChangeProposal/modelChangeProposalErrors';
import { useToast } from 'hephaestus/web';
import { useTranslation } from 'react-i18next';
import { ARCHIVE_HOUSEHOLD_COPY } from './useArchiveHouseholdCopy.query';

/**
 * Retires the household's own copy of a model once the household no longer
 * points at it.
 *
 * Best effort by design: the pointer change it follows has already landed, and
 * Initialize and Edit both send `replaceExistingBase`, so a copy left behind is
 * retired the next time the household's model is set or edited. A failure is
 * logged, and with `warn` said out loud, rather than thrown.
 */
export const useArchiveHouseholdCopy = () => {
  const graphqlClient = useApolloClient();
  const { enqueueToast } = useToast();
  const { t } = useTranslation('workflowCompletions');
  const [archiveEntityBoundModel, { loading }] = useMutation(
    ARCHIVE_HOUSEHOLD_COPY,
  );

  const archiveHouseholdCopy = async (
    householdId: string,
    {
      organizationId: knownOrganizationId,
      warn = true,
    }: {
      organizationId?: string;
      warn?: boolean;
    } = {},
  ) => {
    let organizationId = knownOrganizationId;

    if (!organizationId) {
      // Scoped to the organization that owns the household, which can differ
      // from the active one, so ask the household's own record.
      try {
        organizationId =
          (await loadHouseholdOrganizationId(graphqlClient, householdId)) ??
          undefined;
      } catch (caught) {
        datadogLogs.logger.error(
          'Household model portfolio archive organization lookup failed',
          { clientGroupId: householdId },
          caught instanceof Error ? caught : undefined,
        );
      }
    }

    if (!organizationId) {
      datadogLogs.logger.warn(
        'Household model portfolio archive skipped: no entity organization',
        { clientGroupId: householdId },
      );
      return;
    }

    const variables = {
      input: {
        boundObjectType: BoundObjectType.Household,
        boundObjectId: householdId,
        organizationId,
      },
      // The refusal this can come back with is worded for the advisor below;
      // the error link reads this from variables and would toast it first.
      skipErrorHandler: true,
    } as UseArchiveHouseholdCopyArchiveEntityBoundModelMutationVariables;

    try {
      // `archived: false` says the household had no copy of its own, which is
      // as good an outcome here as having archived one.
      await archiveEntityBoundModel({ variables });
    } catch (error) {
      datadogLogs.logger.error(
        'Household model portfolio archive failed',
        { clientGroupId: householdId },
        error instanceof Error ? error : undefined,
      );
      if (warn) {
        enqueueToast(
          isModelChangeProposalInFlight(error)
            ? t('householdDetach.archiveProposalPending')
            : t('householdDetach.archiveWarning'),
          { variant: 'warning' },
        );
      }
    }
  };

  return { archiveHouseholdCopy, archiveLoading: loading };
};
